import { Fragment, useState, useCallback, useMemo, useEffect } from "react";
import Modal from "../elements/Modal";
import { lngDetect } from "../../helpers/lngDetect";
import Button from "../form/Button";
import Textarea from "../form/Textarea";
import { useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import useForm from "../../hooks/useForm";
import { organizationReviewAddEdit } from "../../services/reviewService";
import { t } from "i18next";

// Constants
const RATING_LABELS = {
  1: "Poor",
  2: "Fair",
  3: "Good",
  4: "Very Good",
  5: "Excellent",
};

const REVIEW_CATEGORIES = {
  OVERALL: "overall",
};

const CATEGORY_LABELS = {
  [REVIEW_CATEGORIES.OVERALL]: "Overall Experience",
};

// Validation rules
const validation = {
  overallRating: [
    {
      required: true,
      message: t("Please select an overall rating"),
      errorType: "required",
    },
  ],
  review: [
    {
      required: true,
      message: t("Please write a review"),
      errorType: "required",
    },
    {
      type: "range",
      min: 10,
      max: 1500,
      message: t("Review must be between 10 and 1500 characters"),
      errorType: "validation",
    },
  ],
};

// Category Rating Component
const CategoryRating = ({
  category,
  label,
  rating,
  onRatingChange,
  disabled = false,
}) => {
  const [hoverRating, setHoverRating] = useState(0);

  const displayRating = hoverRating || rating || 0;

  return (
    <div className="flex flex-col md:flex-row items-start justify-between py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex-1">
        <span className="text-base font-medium text-gray-700">{label}</span>
      </div>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={disabled}
            onClick={() => onRatingChange(category, star)}
            onMouseEnter={() => !disabled && setHoverRating(star)}
            onMouseLeave={() => !disabled && setHoverRating(0)}
            className={`text-lg transition-all duration-200 hover:scale-110 focus:outline-none ${
              disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
            }`}
          >
            <i
              className={`fa-solid fa-star ${
                star <= displayRating ? "text-amber-400" : "text-gray-300"
              }`}
            ></i>
          </button>
        ))}
        <span className="ml-2 text-sm text-gray-600 min-w-[60px]">
          {rating > 0 ? `${rating.toFixed(1)}` : ""}
        </span>
      </div>
    </div>
  );
};

const OrganizationReviewModal = ({
  isOpen,
  onClose = () => {},
  organizationData,
  onSuccess = () => {},
  editData = null, // Review data for editing
  isEdit = false, // Flag to indicate edit mode
}) => {
  const [categoryRatings, setCategoryRatings] = useState({
    [REVIEW_CATEGORIES.OVERALL]: 0,
  });

  const { data: usrData } = useSelector((state) => state?.profile);

  // Initialize useForm - following FormComponent pattern
  const {
    values,
    handleChange,
    handleSubmit,
    errors,
    setFieldsValue,
    formValidate,
  } = useForm({
    validationRules: validation,
    initialState: {
      overallRating: 0,
      review: "",
      status: "approved",
    },
  });

  // Populate form with edit data when in edit mode
  useEffect(() => {
    if (isEdit && editData && isOpen) {
      const rating = editData.rating || editData.originalData?.rating || 0;
      const message = editData.comment || editData.originalData?.message || "";

      setCategoryRatings({
        [REVIEW_CATEGORIES.OVERALL]: rating,
      });

      setFieldsValue({
        overallRating: rating,
        review: message,
      });
    }
  }, [isEdit, editData, isOpen]);

  // Reset form when modal opens/closes
  const handleClose = useCallback(() => {
    // Reset category ratings
    setCategoryRatings({
      [REVIEW_CATEGORIES.OVERALL]: 0,
    });

    // Reset form values
    setFieldsValue({
      overallRating: 0,
      review: "",
    });

    onClose();
  }, [onClose, setFieldsValue]);

  // Handle category rating change
  const handleCategoryRatingChange = useCallback(
    (category, rating) => {
      setCategoryRatings((prev) => ({
        ...prev,
        [category]: rating,
      }));

      // If overall rating is changed, update form value immediately
      if (category === REVIEW_CATEGORIES.OVERALL) {
        handleChange({
          target: { name: "overallRating", value: rating },
        });
      }
    },
    [handleChange]
  );

  // Sync category ratings with form values
  useEffect(() => {
    if (values.overallRating !== categoryRatings[REVIEW_CATEGORIES.OVERALL]) {
      setCategoryRatings((prev) => ({
        ...prev,
        [REVIEW_CATEGORIES.OVERALL]: values.overallRating || 0,
      }));
    }
  }, [values.overallRating, categoryRatings]);

  // Calculate average rating from categories (excluding overall)
  const averageRating = useMemo(() => {
    const categories = [
      // No other categories for now, just overall
    ];

    const ratedCategories = categories.filter(
      (cat) => categoryRatings[cat] > 0
    );
    if (ratedCategories.length === 0) return 0;

    const sum = ratedCategories.reduce(
      (acc, cat) => acc + categoryRatings[cat],
      0
    );
    return sum / ratedCategories.length;
  }, [categoryRatings]);

  // Auto-suggest overall rating based on category averages
  useEffect(() => {
    if (
      averageRating > 0 &&
      categoryRatings[REVIEW_CATEGORIES.OVERALL] === 0 &&
      !isEdit
    ) {
      const suggestedRating = Math.round(averageRating);
      setCategoryRatings((prev) => ({
        ...prev,
        [REVIEW_CATEGORIES.OVERALL]: suggestedRating,
      }));
      handleChange({
        target: { name: "overallRating", value: suggestedRating },
      });
    }
  }, [averageRating, categoryRatings, handleChange, isEdit]);

  // Review submission mutation
  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (val) => {
      if (!usrData) {
        throw new Error("Please login to submit a review");
      }

      if (!categoryRatings[REVIEW_CATEGORIES.OVERALL]) {
        throw new Error("Please select an overall rating");
      }

      // Prepare organization review data
      const payload = {
        referenceId: organizationData?._id,
        rating: categoryRatings[REVIEW_CATEGORIES.OVERALL],
        message: val?.review,
      };

      // Add review ID for editing
      if (isEdit && editData) {
        payload.id = editData.originalData?._id || editData.id;
      }

      return await organizationReviewAddEdit(payload);
    },
    onSuccess: async (res) => {
      if (res?.status === 200 || res?.status === 201) {
        toast.success(
          res?.message ||
            `Review ${isEdit ? "updated" : "submitted"} successfully!`
        );
        onSuccess(res);
        handleClose();
        return res;
      } else {
        toast.warning(res?.message || res?.error);
      }
      return res;
    },
    onError: (error) => {
      toast.error(error?.message || "Something went wrong. Please try again.");
    },
  });

  // Get user display info
  const userDisplayInfo = useMemo(() => {
    if (!usrData) return null;

    const name =
      usrData.name ||
      `${usrData.firstName || ""} ${usrData.lastName || ""}`.trim() ||
      "User";
    const initial = name.charAt(0).toUpperCase();

    return { name, initial };
  }, [usrData]);

  const organizationName =
    lngDetect(organizationData?.name) ||
    lngDetect(organizationData?.eventSetting?.title) ||
    "this organizer";

  return (
    <Fragment>
      <Modal
        isOpen={isOpen}
        setIsOpen={handleClose}
        title={`${t(isEdit ? "Edit" : "Review")} ${organizationName}`}
        size="xl4"
        titleIcon={isEdit ? "fa-regular fa-edit" : "fa-regular fa-star"}
        titleClass="!font-semibold !text-2xl !capitalize truncate max-w-[700px] w-full tracking-tight !text-deep-purple"
        headerClass="!text-black !rounded-t-2xl border-b border-surface-03 bg-primary/10 py-4 px-10"
        childrenClasses="!bg-white"
        bodyClasses={"!py-8 md:!px-10"}
      >
        <div className="max-w-3xl mx-auto">
          {/* Flex container for header + rating */}
          <div className="flex flex-col md:flex-row gap-6 mb-10">
            {/* Left: Header / Logo */}
            <div className="flex-1 text-start md:text-left ">
              <div className="flex items-start justify-start mb-4">
                {organizationData?.logo?.url && (
                  <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-gray-200">
                    <img
                      src={organizationData.logo.url}
                      alt={organizationName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                {t(isEdit ? "Update Your Review" : "Share Your Experience")}
              </h3>
              <p className="text-gray-600">
                {t(
                  isEdit
                    ? "Make changes to your review for"
                    : "How was your overall experience with"
                )}
                <span className="font-bold text-deep-purple ml-1">
                  {organizationName}
                </span>
                ?
              </p>
              {isEdit && (
                <div className="mt-2 px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full inline-block">
                  <i className="fa-solid fa-edit mr-1"></i>
                  {t("Editing your review")}
                </div>
              )}
            </div>

            {/* Right: Rating Card */}
            <div className="flex-1">
              <div className="bg-gradient-to-br from-gray-100 to-gray-50 rounded-xl border border-gray-300 p-6">
                <h4 className="text-lg font-semibold text-gray-800 mb-4 border-b border-gray-300 pb-2">
                  {t(isEdit ? "Update Your Rating" : "Rate Your Experience")}
                </h4>

                <div className="space-y-1">
                  {Object.entries(CATEGORY_LABELS).map(([category, label]) => (
                    <CategoryRating
                      key={category}
                      category={category}
                      label={label}
                      rating={categoryRatings[category]}
                      onRatingChange={handleCategoryRatingChange}
                      disabled={isLoading}
                    />
                  ))}
                </div>

                {/* Average Rating Display */}
                {averageRating > 0 && !isEdit && (
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-amber-800">
                        {t("Category Average")}: {averageRating.toFixed(1)}
                      </span>
                      <span className="text-xs text-amber-600">
                        {RATING_LABELS[Math.round(averageRating)]}
                      </span>
                    </div>
                  </div>
                )}

                {/* Overall Rating Error */}
                {formValidate(errors, "overallRating").errorText && (
                  <div className="text-xs text-red-500 mt-2">
                    <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
                    {formValidate(errors, "overallRating").errorText}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={(event) => handleSubmit(event, mutate)}>
            <div className="grid grid-cols-12 gap-x-4 gap-y-6">
              {/* Rating Categories Section */}
              {/* <div className="col-span-12">
                <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-100 p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">
                    {isEdit ? "Update Your Rating" : "Rate Your Experience"}
                  </h4>

                  <div className="space-y-1">
                    {Object.entries(CATEGORY_LABELS).map(
                      ([category, label]) => (
                        <CategoryRating
                          key={category}
                          category={category}
                          label={label}
                          rating={categoryRatings[category]}
                          onRatingChange={handleCategoryRatingChange}
                          disabled={isLoading}
                        />
                      )
                    )}
                  </div>

                  {averageRating > 0 && !isEdit && (
                    <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-amber-800">
                          Category Average: {averageRating.toFixed(1)}
                        </span>
                        <span className="text-xs text-amber-600">
                          {RATING_LABELS[Math.round(averageRating)]}
                        </span>
                      </div>
                    </div>
                  )}

                  {formValidate(errors, "overallRating").errorText && (
                    <div className="text-xs text-red-500 mt-2">
                      <i className="fa-regular fa-fw fa-triangle-exclamation text-sm mr-1"></i>
                      {formValidate(errors, "overallRating").errorText}
                    </div>
                  )}
                </div>
              </div> */}

              {/* Written Review Section */}
              <div className="col-span-12">
                <Textarea
                  label={t(
                    isEdit
                      ? "Update Your Detailed Review"
                      : "Share Your Detailed Review"
                  )}
                  inputPlaceholder={t(
                    "Tell others about your experience with this organizer. What did you like? What could be improved? Would you attend their events again?"
                  )}
                  inputName="review"
                  inputClasses={"h-26"}
                  labelClasses={"text-md"}
                  inputValue={values.review || ""}
                  onChange={handleChange}
                  rows={6}
                  maxLength={1500}
                  {...formValidate(errors, "review")}
                />
                <div className="text-right text-sm text-gray-500 mt-2">
                  {(values.review || "").length}/1500 {t("characters")}
                </div>
              </div>

              {/* User Info Display */}
              {userDisplayInfo && (
                <div className="col-span-12">
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-sm font-semibold">
                          {userDisplayInfo.initial}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-gray-800">
                          {userDisplayInfo.name}
                        </div>
                        <div className="text-sm text-gray-600">
                          {t("This review will be")}{" "}
                          {t(isEdit ? "updated" : "posted")} {t("publicly")}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Guidelines */}
              <div className="col-span-12">
                <div className="p-4 bg-green-50 rounded-xl border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-2">
                    <i className="fa-regular fa-lightbulb mr-2"></i>
                    {t("Organization Review Guidelines")}
                  </h4>
                  <ul className="text-sm text-green-700 space-y-1">
                    <li>
                      •{" "}
                      {t(
                        "Rate based on your overall experience with their events"
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "Consider organization, communication, and event quality"
                      )}
                    </li>
                    <li>
                      • {t("Be honest and constructive in your feedback")}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "Help others make informed decisions about attending their events"
                      )}
                    </li>
                    <li>
                      •{" "}
                      {t(
                        "Focus on the organizer's professionalism and service quality"
                      )}
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="relative col-span-4 mt-8">
              <div className="flex items-center justify-end gap-4">
                <Button
                  buttonType="submit"
                  buttonLabel={t(isEdit ? "Update Review" : "Submit Review")}
                  variant="secondary"
                  {...(isLoading && {
                    loading: true,
                    buttonIconPosition: "left",
                    isDisable: true,
                  })}
                />
                <Button
                  buttonLabel={t("Cancel")}
                  variant="primary"
                  buttonFunction={handleClose}
                />
              </div>
            </div>
          </form>
        </div>
      </Modal>
    </Fragment>
  );
};

export default OrganizationReviewModal;
